import { getMaxArrayNymber, IContainerConfig, isDefined } from "@dhx/ts-common/core";
import {
	ICol,
	IColumnsWidth,
	IGridConfig,
	IRendererConfig,
	IRow,
	colType,
	AdjustTargetType,
} from "./../types";
import { removeHTMLTags } from "./main";
import { stringToDate, getFormattedDate } from "@dhx/ts-common/date";

function normalizeArray(obj: any, name: string) {
	if (!obj[name]) {
		return;
	}
	if (typeof obj[name] === "string") {
		obj[name] = [
			{
				text: `${obj[name]}`,
			},
		];
	} else {
		obj[name] = obj[name].map(el => {
			if (typeof el === "string") {
				el = { text: el } as any;
			}
			return el;
		});
	}
}

export function normalizeColumns({ columns, htmlEnable }: IGridConfig) {
	for (const col of columns) {
		col.$htmlEnable = !!(col.htmlEnable || htmlEnable);
		col.$cellCss = col.$cellCss || {};
		normalizeArray(col, "header");
		normalizeArray(col, "footer");
		const isContent = col.header.reduce((acc, item) => (acc = acc || !!item.content), false);
		if (isContent) {
			col.$uniqueData = [];
		}

		let width = col.minWidth || 100;
		if (col.width) {
			if (col.maxWidth && col.minWidth) {
				width =
					col.width >= col.minWidth && col.width <= col.maxWidth
						? col.width
						: col.width >= col.maxWidth
						? col.maxWidth
						: col.minWidth;
			} else if (col.maxWidth) {
				width = col.width <= col.maxWidth ? col.width : col.maxWidth > 100 ? col.maxWidth : 100;
			} else if (col.minWidth) {
				width = col.width >= col.minWidth ? col.width : col.minWidth;
			} else {
				width = col.width;
			}
		}

		col.$width = col.$width || width;
		if (col.$width > col.maxWidth) {
			col.$width = col.maxWidth;
		}
		if (col.$width < col.minWidth) {
			col.$width = col.minWidth;
		}
	}
}

export function countColumns(config: IGridConfig, columns: ICol[]): number {
	let headerRowsCount = 0;
	let footerRowsCount = 0;
	let totalWidth = 0;
	let colspans = false;
	let rowsHeadersCount = 0;
	let footer = false;
	columns.forEach(col => {
		headerRowsCount = Math.max(headerRowsCount, col.header.length);
		totalWidth += col.$width;

		if (col.footer) {
			footerRowsCount = Math.max(footerRowsCount, col.footer.length);
			if (!footer) {
				footer = true;
			}
		}

		if (!colspans) {
			for (const head of col.header) {
				if (head.colspan) {
					colspans = true;
					return;
				}
			}
		}
	});

	// fill missing cells
	columns.forEach(col => {
		if (col.header.length < headerRowsCount) {
			for (let i = 0; i < headerRowsCount; i++) {
				col.header[i] = col.header[i] || { text: "" };
			}
		}
		if (footer) {
			col.footer = col.footer || [];
		}
		if (col.footer && col.footer.length < footerRowsCount) {
			for (let i = 0; i < footerRowsCount; i++) {
				col.footer[i] = col.footer[i] || { text: "" };
			}
		}
		col.header = col.header.map(head => {
			if (typeof head !== "object") {
				head = { text: head };
			}
			head.css = head.css || "";
			if (!head.text && !head.css.includes("dhx_cell-empty")) {
				head.css += " dhx_cell-empty";
			}
			return head;
		});

		// find header columns indexes
		if (col.header[0].text === "") {
			rowsHeadersCount++;
		}
	});
	config.$totalWidth = totalWidth;
	config.$headerLevel = headerRowsCount;
	config.$footerLevel = footerRowsCount;
	config.$colspans = colspans;
	config.$footer = footer;

	return rowsHeadersCount;
}

export function calculatePositions(
	width: number,
	height: number,
	scroll: any,
	conf: IRendererConfig,
	data: IRow[]
) {
	const columns = conf.columns || [];
	const columnsLength = columns.length;
	const rows = data || [];
	const rowsLength = rows.length;

	let maxWidth = -Infinity;
	for (let index = 0; index < columnsLength; index++) {
		if (columns[index].$width > maxWidth) maxWidth = columns[index].$width;
	}

	let minWidth = +Infinity;
	for (let index = 0; index < columnsLength; index++) {
		if (columns[index].$width < minWidth) minWidth = columns[index].$width;
	}

	let maxHeight = -Infinity;
	for (let index = 0; index < rowsLength; index++) {
		if (rows[index].$height > maxHeight) maxHeight = rows[index].$height;
	}
	const minHeight = conf.rowHeight;

	const xReserve = Math.round(maxWidth / minWidth);
	const yReserve = Math.round(maxHeight / minHeight);

	const avrColWidth = conf.$totalWidth / columnsLength;
	const colPerPage = Math.round(width / avrColWidth);

	const avrRowHeight = conf.$totalHeight / rowsLength;
	const rowPerPage = Math.round(height / avrRowHeight);

	let x = 0;
	let scrollLeft = scroll.left;

	for (let i = 0; i < columns.length; i++) {
		const col = columns[i];
		scrollLeft = scrollLeft - col.$width;
		if (scrollLeft + avrColWidth / 2 > 0) {
			x++;
		} else {
			break;
		}
	}

	let y = 0;
	let scrollTop = scroll.top;

	for (let i = 0; i < rows.length; i++) {
		const row = rows[i];
		scrollTop = scrollTop - row.$height;
		if (scrollTop + avrRowHeight / 2 > 0) {
			y++;
		} else {
			break;
		}
	}

	const xStart = x - xReserve >= 0 ? x - xReserve : 0;
	const xEnd = x + colPerPage + xReserve;

	const yStart = y - yReserve >= 0 ? y - yReserve : 0;
	const yEnd = y + rowPerPage + yReserve;

	return {
		xStart,
		xEnd,
		yStart,
		yEnd,
	};
}

export function getUnique(arr: any[], name: string, multiselection: boolean): any[] {
	const allItems = arr.map(item => item[name]);
	if (multiselection) {
		allItems.forEach((item, index) => {
			if (item.includes(", ")) {
				item.split(", ").forEach(i => allItems.push(i));
				delete allItems[index];
			}
		});
	}
	return allItems.filter((item, i, array) => array.indexOf(item) === i && isDefined(item)).sort();
}

interface IDefinedColumns {
	width: number;
	htmlEnable: boolean;
	template?: (value: string | number, row: IRow, col: ICol) => string;
	cols?: ICol;
	format?: string;
}

export const getMaxRowHeight = (
	row: IRow,
	cols: ICol[],
	config: IContainerConfig = { font: "20px Roboto", lineHeight: 20 }
): number => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d", {
		alpha: false,
	});
	ctx.font = config.font;

	const definedColumns: { [col: string]: IDefinedColumns } = {};
	const colLength = cols.length;

	for (let index = 0; index < colLength; index++) {
		if (cols[index].template) {
			definedColumns[cols[index].id] = {
				width: cols[index].$width || 0,
				htmlEnable: cols[index].$htmlEnable,
				template: cols[index].template,
				cols: cols[index],
			};
		} else {
			definedColumns[cols[index].id] = {
				width: cols[index].$width || 0,
				htmlEnable: cols[index].$htmlEnable,
			};
		}
	}

	const defaultLineBreak = [];
	const mathLineBreak = [];

	for (const [key, value] of Object.entries(row)) {
		if (
			definedColumns[key] &&
			key !== "id" &&
			key !== "height" &&
			!key.startsWith("$") &&
			(typeof value === "string" || typeof value === "number")
		) {
			let currentValue = "";
			if (definedColumns[key]?.template) {
				const templateValue = definedColumns[key].template(value, row, definedColumns[key].cols);
				currentValue = definedColumns[key].htmlEnable ? removeHTMLTags(templateValue) : templateValue;
			} else if (typeof value === "string") {
				if (definedColumns[key].htmlEnable) {
					currentValue = removeHTMLTags(value);
				} else {
					currentValue = value;
				}
			} else {
				currentValue = value.toString();
			}

			defaultLineBreak.push(currentValue.split("\n").length);
			mathLineBreak.push(Math.round(ctx.measureText(currentValue).width / definedColumns[key]?.width));
		}
	}
	const maxRows = Math.max(getMaxArrayNymber(defaultLineBreak), getMaxArrayNymber(mathLineBreak));
	canvas.remove();

	return maxRows * config.lineHeight;
};

export const getCalculatedRowHeight = (
	height: number,
	config = {
		rowHeight: 40,
		padding: 8,
	}
) => {
	const calculateHeight = config.rowHeight < 40 ? height : height + config.padding * 2;
	return height < config.rowHeight ? config.rowHeight : calculateHeight;
};

export const getTreeCellWidthOffset = (row: IRow): number => {
	return 20 + row.$level * 20 - (row.$items ? 20 : 0);
};

export const getMaxColsWidth = (
	rows: IRow[],
	cols: ICol[],
	config: IContainerConfig = {
		font: "normal 14.4px Arial",
	},
	target?: AdjustTargetType
): IColumnsWidth => {
	if (!rows.length || !cols.length) return;

	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d", {
		alpha: false,
	});

	ctx.font = config.font;

	const definedColumns: { [col: string]: IDefinedColumns } = {};
	const colLength = cols.length;

	for (let index = 0; index < colLength; index++) {
		if (cols[index].template && target === "data") {
			definedColumns[cols[index].id] = {
				width: 20,
				htmlEnable: cols[index].$htmlEnable,
				template: cols[index].template,
				cols: cols[index],
			};
		} else {
			definedColumns[cols[index].id] = {
				width: 20,
				htmlEnable: cols[index].$htmlEnable,
				format: cols[index].format,
			};
		}
	}

	const rowsLength = rows.length;

	for (let index = 0; index < rowsLength; index++) {
		for (const [key, value] of Object.entries(rows[index])) {
			if (
				definedColumns[key] &&
				key !== "id" &&
				key !== "height" &&
				!key.startsWith("$") &&
				(typeof value === "string" || typeof value === "number" || value instanceof Date)
			) {
				let currentValue;
				if (typeof definedColumns[key]?.template === "function" && !(value instanceof Date)) {
					const templateValue = definedColumns[key].template(
						value,
						rows[index],
						definedColumns[key].cols
					);
					currentValue = definedColumns[key].htmlEnable
						? removeHTMLTags(templateValue)
						: templateValue;
				} else if (value instanceof Date) {
					currentValue = getFormattedDate(definedColumns[key].format || "%M %d %Y", value);
				} else {
					currentValue = definedColumns[key].htmlEnable
						? removeHTMLTags(value as string)
						: value.toString();
				}

				const width = ctx.measureText(currentValue).width;
				if (width > definedColumns[key].width) definedColumns[key].width = width;
			}
		}
	}
	canvas.remove();

	const totalColumns = {};
	for (const [key, value] of Object.entries(definedColumns)) {
		totalColumns[key] = Math.ceil(value.width);
	}

	return totalColumns;
};

export function toFormat(value: any, type?: colType, format?: string) {
	if (!value && typeof value !== "number") {
		return value;
	}
	const formatTemplate = (type: colType) => {
		value = value.toString();
		let result;
		const template = format
			.replace(/#+/g, "#")
			.split("#")
			.filter(i => i);

		value = type === "percent" ? (Number(value) * 100).toString() : value;
		const trunc = Math.trunc(Number(value)).toString();
		const formatFractionLength = format.match(/0/g) && format.match(/0/g).length;

		const truncTemplate = template.find(i => !i.includes("0"));
		result = truncTemplate ? trunc.replace(/(\d)(?=(\d{3})+(\D|$))/g, `$1${truncTemplate}`) : trunc;

		if (formatFractionLength) {
			const delimiter = template.find(i => i.includes("0")).replace(/0+/g, "");
			const truncFractional = value.split(".")[1] || "0";

			let fractional = Number("0." + truncFractional).toFixed(formatFractionLength);
			if (Number(fractional) >= 1) (result++).toString();
			fractional = fractional
				.toString()
				.split(".")[1]
				.padEnd(formatFractionLength, "0");

			result += delimiter + fractional;
		}

		return type === "percent" ? `${result}%` : result;
	};

	switch (type) {
		case "number":
		case "percent":
			if (!format) format = "#";
			return isDefined(value) && !isNaN(Number(value)) ? formatTemplate(type) : value;
		case "date":
			if (!format) format = "%M %d %Y";
			if (typeof value === "string") {
				value = getFormattedDate(format, stringToDate(value, format));
			} else if (typeof value === "object") {
				value = getFormattedDate(format, value);
			}
			return value;
		default:
			return value;
	}
}
